import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { cx } from './cx.js'
import styles from './Input.module.css'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  /**
   * Help text under the field — a unit, a format, an example. `DES-001`: the
   * alternative callers reached for was putting the explanation in `label`,
   * which `.label` then uppercases into a shouted sentence.
   */
  hint?: string
}

/**
 * `DES-001` — three additions, all additive, none changing any existing call
 * site's rendering or behaviour:
 *
 * - `role="alert"` on the error paragraph. `aria-invalid` alone marks the
 *   field as bad but announces nothing, so a server refusal rendered here was
 *   silent to a screen reader — the user least able to spot a red border.
 * - `aria-describedby`, so the hint and the error are actually read out with
 *   the field rather than merely sitting near it.
 * - `forwardRef`, needed the moment this primitive meets `react-hook-form`'s
 *   own `register()` (the operator `LoginPage`/`ResetPasswordPage` audit
 *   fix): `register()` returns a `ref` callback RHF needs attached to the
 *   real `<input>` for focus management and validation triggers, and under
 *   React 18 a plain function component cannot receive one — it warns and
 *   silently drops it. No existing caller passes a `ref` today, so this
 *   changes nothing for them.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className, ...rest },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`

  const describedBy = [hint !== undefined && hintId, error !== undefined && errorId]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')

  return (
    <div className={styles['field']}>
      <label htmlFor={inputId} className={styles['label']}>
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className={cx(styles['input'], error && styles['invalid'], className)}
        aria-invalid={error !== undefined}
        {...(describedBy === '' ? {} : { 'aria-describedby': describedBy })}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className={styles['hint']}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} role="alert" className={styles['error']}>
          {error}
        </p>
      )}
    </div>
  )
})
